/**
 * AI Control Plane — 统一 AI 管控面板
 *
 * Single-pane-of-glass for all AI/Brain/Skill operations:
 *   ① Brain Health  — live status, uptime, last heartbeat
 *   ② Model Registry — registered models, endpoints, fallback chains
 *   ③ Active Skills  — enabled skills, execution stats
 *   ④ Recent Actions — last N brain actions / tool calls
 *   ⑤ Capability Summary — role-capability matrix
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Activity,
  Brain,
  Cpu,
  Zap,
  Shield,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Server,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────

interface BrainStatus {
  status: string;
  uptime?: number;
  lastHeartbeat?: string;
  activeSkills?: number;
  tenantId?: string;
  version?: string;
}

interface ModelEntry {
  id: string;
  model_id: string;
  provider: string;
  display_name?: string;
  is_default?: boolean;
  status?: string;
}

interface SkillEntry {
  id: string;
  name: string;
  skill_key: string;
  status: string;
  last_run?: string;
  run_count?: number;
}

interface RecentAction {
  id: string;
  action: string;
  skill?: string;
  status: string;
  created_at: string;
  duration_ms?: number;
}

interface CapabilitySummary {
  total: number;
  enabled: number;
  byCategory: Record<string, number>;
}

// ─── Helpers ──────────────────────────────────────────────────────

const api = async (url: string) => {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) return null;
  const body = await res.json();
  return body?.data ?? body;
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { color: string; icon: React.ReactNode }> = {
    running: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3.5 h-3.5" /> },
    healthy: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3.5 h-3.5" /> },
    active: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3.5 h-3.5" /> },
    enabled: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3.5 h-3.5" /> },
    stopped: { color: 'bg-red-100 text-red-800', icon: <XCircle className="w-3.5 h-3.5" /> },
    error: { color: 'bg-red-100 text-red-800', icon: <XCircle className="w-3.5 h-3.5" /> },
    failed: { color: 'bg-red-100 text-red-800', icon: <XCircle className="w-3.5 h-3.5" /> },
    disabled: { color: 'bg-gray-100 text-gray-600', icon: <XCircle className="w-3.5 h-3.5" /> },
    warning: { color: 'bg-yellow-100 text-yellow-800', icon: <AlertTriangle className="w-3.5 h-3.5" /> },
  };
  const s = status?.toLowerCase() ?? 'unknown';
  const { color, icon } = map[s] ?? { color: 'bg-gray-100 text-gray-600', icon: <Clock className="w-3.5 h-3.5" /> };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {icon} {status}
    </span>
  );
}

function Card({ title, icon, children, actions }: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          {icon}
          {title}
        </div>
        {actions}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────

export default function AiControlPlanePage() {
  const [brain, setBrain] = useState<BrainStatus | null>(null);
  const [models, setModels] = useState<ModelEntry[]>([]);
  const [skills, setSkills] = useState<SkillEntry[]>([]);
  const [actions, setActions] = useState<RecentAction[]>([]);
  const [capabilities, setCapabilities] = useState<CapabilitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const refresh = useCallback(async () => {
    setLoading(true);
    const [brainData, modelData, skillData, actionData, capData] = await Promise.all([
      api('/api/v1/brain-soul/status'),
      api('/api/v1/model-registry'),
      api('/api/v1/skills'),
      api('/api/v1/brain-soul/actions?limit=20'),
      api('/api/v1/capability/definitions'),
    ]);

    if (brainData) setBrain(brainData);
    if (Array.isArray(modelData)) setModels(modelData);
    else if (modelData?.items) setModels(modelData.items);
    if (Array.isArray(skillData)) setSkills(skillData);
    else if (skillData?.items) setSkills(skillData.items);
    if (Array.isArray(actionData)) setActions(actionData.slice(0, 20));
    else if (actionData?.items) setActions(actionData.items.slice(0, 20));

    if (capData) {
      const defs = Array.isArray(capData) ? capData : (capData?.items ?? []);
      const byCategory: Record<string, number> = {};
      for (const d of defs) {
        const cat = (d as any).category || 'other';
        byCategory[cat] = (byCategory[cat] || 0) + 1;
      }
      setCapabilities({
        total: defs.length,
        enabled: defs.filter((d: any) => d.is_enabled !== false).length,
        byCategory,
      });
    }

    setLastRefresh(new Date());
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const formatUptime = (sec?: number) => {
    if (!sec) return '—';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '—';
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  };

  const enabledSkills = skills.filter(s => s.status === 'active' || s.status === 'enabled');
  const defaultModel = models.find(m => m.is_default);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Cpu className="w-6 h-6 text-indigo-600" />
            AI Control Plane
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            统一 AI 管控面板 — Brain · Models · Skills · Actions
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          刷新
        </button>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          {
            label: 'Brain Status',
            value: brain?.status ?? '—',
            badge: true,
            icon: <Brain className="w-5 h-5 text-purple-600" />,
          },
          {
            label: 'Uptime',
            value: formatUptime(brain?.uptime),
            icon: <Clock className="w-5 h-5 text-blue-600" />,
          },
          {
            label: 'Active Skills',
            value: `${enabledSkills.length} / ${skills.length}`,
            icon: <Zap className="w-5 h-5 text-amber-600" />,
          },
          {
            label: 'Models',
            value: `${models.length}`,
            sub: defaultModel?.display_name ?? defaultModel?.model_id,
            icon: <Server className="w-5 h-5 text-teal-600" />,
          },
          {
            label: 'Capabilities',
            value: `${capabilities?.enabled ?? 0} / ${capabilities?.total ?? 0}`,
            icon: <Shield className="w-5 h-5 text-indigo-600" />,
          },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
              {stat.icon} {stat.label}
            </div>
            {stat.badge ? (
              <StatusBadge status={String(stat.value)} />
            ) : (
              <div className="text-lg font-semibold text-gray-900">{stat.value}</div>
            )}
            {stat.sub && <div className="text-xs text-gray-400 mt-0.5 truncate">{stat.sub}</div>}
          </div>
        ))}
      </div>

      {/* Main panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Model Registry */}
        <Card title="Model Registry" icon={<Server className="w-4 h-4 text-teal-600" />}>
          {models.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No models registered</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {models.map(m => (
                <div key={m.id} className="py-2.5 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">
                      {m.display_name ?? m.model_id}
                      {m.is_default && (
                        <span className="ml-1.5 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">
                          DEFAULT
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{m.provider} · {m.model_id}</div>
                  </div>
                  <StatusBadge status={m.status ?? 'active'} />
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Active Skills */}
        <Card title="Active Skills" icon={<Zap className="w-4 h-4 text-amber-600" />}>
          {skills.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No skills configured</p>
          ) : (
            <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
              {skills.slice(0, 15).map(s => (
                <div key={s.id} className="py-2 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{s.name || s.skill_key}</div>
                    {s.last_run && (
                      <div className="text-xs text-gray-400">
                        Last run: {new Date(s.last_run).toLocaleString()} · {s.run_count ?? 0} runs
                      </div>
                    )}
                  </div>
                  <StatusBadge status={s.status} />
                </div>
              ))}
              {skills.length > 15 && (
                <div className="text-xs text-gray-400 pt-2 text-center">+{skills.length - 15} more</div>
              )}
            </div>
          )}
        </Card>

        {/* Recent Actions */}
        <Card title="Recent Actions" icon={<Activity className="w-4 h-4 text-rose-600" />}>
          {actions.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No recent actions</p>
          ) : (
            <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {actions.map(a => (
                <div key={a.id} className="py-2 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm text-gray-800 truncate">{a.action}</div>
                    <div className="text-xs text-gray-400">
                      {a.skill && <span className="text-indigo-500">{a.skill}</span>}
                      {a.skill && ' · '}
                      {new Date(a.created_at).toLocaleString()}
                      {a.duration_ms != null && ` · ${formatDuration(a.duration_ms)}`}
                    </div>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Capability Summary */}
        <Card title="Capability Matrix" icon={<Shield className="w-4 h-4 text-indigo-600" />}>
          {!capabilities || capabilities.total === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No capabilities defined</p>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Total:</span>{' '}
                  <span className="font-semibold">{capabilities.total}</span>
                </div>
                <div>
                  <span className="text-gray-500">Enabled:</span>{' '}
                  <span className="font-semibold text-green-700">{capabilities.enabled}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(capabilities.byCategory).map(([cat, count]) => (
                  <div
                    key={cat}
                    className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm"
                  >
                    <span className="text-gray-700 capitalize">{cat}</span>
                    <span className="font-medium text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Footer */}
      <div className="text-xs text-gray-400 text-center">
        Last refreshed: {lastRefresh.toLocaleTimeString()}
      </div>
    </div>
  );
}
